/**
 * Tracks the chats this agent invited a visitor into, so the "… has joined the
 * chat" notice the backend posts when the visitor turns up can be surfaced as a
 * toast for those chats only.
 *
 * The invite is sent against a visitor (no room exists yet), and the join notice
 * arrives against a room, so the link is made when the room is created with an
 * invited visitor in it.
 */

/** Long enough for a visitor to notice and accept an invite, short enough to forget stale ones. */
export const INVITED_CHAT_TTL_MS = 30 * 60_000;

const invitedVisitorExpiry = new Map();
const invitedRoomExpiry = new Map();

function remember(store, id) {
  if (!id) return;
  store.set(String(id), Date.now() + INVITED_CHAT_TTL_MS);
}

function isRemembered(store, id) {
  if (!id) return false;
  const key = String(id);
  const expiresAt = store.get(key);
  if (!expiresAt) return false;
  if (Date.now() > expiresAt) {
    store.delete(key);
    return false;
  }
  return true;
}

/**
 * @param {string | null | undefined} visitorId
 */
export function rememberInvitedVisitor(visitorId) {
  remember(invitedVisitorExpiry, visitorId);
}

/**
 * @param {string | null | undefined} visitorId
 * @returns {boolean}
 */
export function isInvitedVisitor(visitorId) {
  return isRemembered(invitedVisitorExpiry, visitorId);
}

/**
 * Marks a freshly created room as invited when any of its users is a visitor
 * this agent invited.
 *
 * @param {Record<string, any> | null | undefined} room
 * @returns {boolean} whether the room came from an invite
 */
export function rememberInvitedRoomFromRoom(room) {
  const roomId = room?._id || room?.id;
  if (!roomId) return false;

  const users = Array.isArray(room?.users) ? room.users : [];
  const fromInvite = users.some((user) =>
    isInvitedVisitor(typeof user === "string" ? user : user?._id)
  );
  if (!fromInvite) return false;

  remember(invitedRoomExpiry, roomId);
  return true;
}

/**
 * @param {string | null | undefined} roomId
 * @returns {boolean}
 */
export function isInvitedRoom(roomId) {
  return isRemembered(invitedRoomExpiry, roomId);
}

/**
 * True while any invite this agent sent could still be answered.
 *
 * Linking an invite to its room needs `create-room` to arrive with the invited
 * visitor in `users`, which does not always happen. A recent unanswered invite
 * is the weaker but reliable signal that an arriving visitor is the one we asked
 * for, so it is enough on its own to justify the toast.
 *
 * @returns {boolean}
 */
export function hasOutstandingInvite() {
  for (const visitorId of [...invitedVisitorExpiry.keys()]) {
    if (isRemembered(invitedVisitorExpiry, visitorId)) return true;
  }
  return false;
}

/** Visitor statuses that mean they are now in a chat rather than still browsing. */
const JOINED_STATUSES = new Set(["chatting", "joined", "in_chat", "active"]);

const announcedVisitorIds = new Set();

/**
 * Whether an invited visitor has just turned up and has not been announced yet.
 *
 * `visitor_status_updated` repeats as the visitor moves around, so the announce
 * is one-shot per visitor.
 *
 * @param {string | null | undefined} visitorId
 * @param {{ status?: string, currentRoom?: string | null }} update
 * @returns {boolean}
 */
export function shouldAnnounceInvitedVisitorJoin(visitorId, update = {}) {
  if (!isInvitedVisitor(visitorId)) return false;

  const key = String(visitorId);
  if (announcedVisitorIds.has(key)) return false;

  const status = String(update.status || "").toLowerCase();
  const joined = Boolean(update.currentRoom) || JOINED_STATUSES.has(status);
  if (!joined) return false;

  announcedVisitorIds.add(key);
  return true;
}

/** @internal Test helper */
export function clearInvitedChats() {
  invitedVisitorExpiry.clear();
  invitedRoomExpiry.clear();
  announcedVisitorIds.clear();
}
